/**
 * AREA PLAN vs ACTUAL
 *
 * The same weekly grid as Area Plan Create, but every area gets TWO lanes:
 *
 *   Plan    - every area_planner row for that room, derived from the equipment
 *             plan via equipment_models.area_id
 *   Actual  - every equipment_log_books row whose equipment_id IS that room's
 *             PRR code, for the window shown
 *
 * The two lanes are deliberately INDEPENDENT. The actual lane is simply what
 * the log book recorded for that PRR number - nothing is matched, joined or
 * scored against the plan. You read the comparison by eye: the top bar is what
 * was meant to happen, the bar underneath is what did.
 *
 * That is a deliberate change from how the tracker works. The endpoint this
 * page calls also computes a batch-level match (AREA_ID|BATCH), but that match
 * is not used here, because the plan books a batch onto ONE machine while the
 * batch physically passes through several rooms - batch CATCB26113 is planned
 * only in PRR016 but was logged in PRR016, PRR038, PRR005 and PRR040. Scoring
 * the actual lane against the plan therefore flags most real work as an
 * exception, which is noise. Blocks are coloured by activity type only.
 *
 * VIEW CONTROLS - display only, none of them change what is fetched:
 *   - "Rooms with activity only" hides rooms with nothing on either lane. The
 *     room count in the header always states how many are hidden and offers a
 *     one-click way back, so the filter cannot quietly mislead.
 *   - Search matches the label, the PRR code and the full room name.
 *   - The legend swatches are toggles: click one to drop that activity type.
 *   - A red line marks the current time, ticking once a minute.
 *
 * TABLET / TOUCH
 * This screen is used on tablets on the shop floor, so:
 *   - Every dimension that costs vertical space collapses at narrow widths -
 *     the header is two rows on a desktop and stays legible down to ~700px.
 *   - Hover is not available on touch. A bar can be TAPPED to pin its detail
 *     card, which renders as a bottom sheet on small screens rather than a
 *     floating tooltip. Hover still works where a mouse exists.
 *
 * Read-only. Nothing on this page writes.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DatePicker, Button, Spin, Empty, Alert, Tag, Input, Switch, Space } from 'antd';
import dayjs from 'dayjs';
import { fetchAreaPlannerAreaList, fetchAreaActivitiesByArea } from './api';

const HOURS_PER_DAY = 24;
const TOTAL_DAYS = 7;
const TOTAL_HOURS = HOURS_PER_DAY * TOTAL_DAYS; // 168
const TOTAL_MINUTES = TOTAL_HOURS * 60;
const MIN_BLOCK_PERCENT = 0.32; // keeps very short activities visible

// Widths collapse in two steps: a desktop, a tablet in landscape (< 1100) and
// a tablet in portrait or a phone (< 760).
const BREAK_COMPACT = 1100;
const BREAK_TIGHT = 760;

// Same palette as the plan grid, so a colour means the same thing on both pages.
const ACTIVITY_COLORS = {
  manufacturing: '#2563eb',
  cleaning: '#059669',
  maintenance: '#d97706',
  other: '#6b7280',
};

// [type, full label, short label used when space is tight]
const LEGEND = [
  ['manufacturing', 'Manufacturing', 'Mfg'],
  ['cleaning', 'Cleaning', 'Clean'],
  ['maintenance', 'Maintenance', 'Maint'],
];

const colorFor = (type) => ACTIVITY_COLORS[type] || ACTIVITY_COLORS.other;

/** The API sends 'YYYY-MM-DD HH:mm:ss.SSS ZZ'; dayjs parses it, offset included. */
const parseApiTime = (value) => {
  if (!value || value === 'running') return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Viewport width, updated on resize AND orientation change - a tablet turned
 * sideways fires the latter and must re-lay-out.
 * Coalesced through rAF so a drag-resize does not thrash React.
 */
const useViewportWidth = () => {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let frame = null;
    const onResize = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setWidth(window.innerWidth);
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return width;
};

const AreaPlanVsActual = () => {
  const [areaList, setAreaList] = useState([]);
  const [areaData, setAreaData] = useState({});
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null); // { x, y, block } - mouse only
  const [pinned, setPinned] = useState(null); // block, set by tap/click
  const [hoveredArea, setHoveredArea] = useState(null);

  // View controls
  const [search, setSearch] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [mutedTypes, setMutedTypes] = useState({}); // { cleaning: true } = hidden
  const [showHelp, setShowHelp] = useState(false);

  /* ---------------------------------------------------------------- *
   *  Responsive sizing
   * ---------------------------------------------------------------- */
  const viewportWidth = useViewportWidth();
  const compact = viewportWidth < BREAK_COMPACT;
  const tight = viewportWidth < BREAK_TIGHT;

  // No hover on a touch screen, so the tooltip cannot be the only way in.
  const isTouch = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none)').matches;
  }, []);

  const areaLabelWidth = tight ? 104 : compact ? 128 : 158;
  const laneLabelWidth = tight ? 50 : compact ? 58 : 68;
  const laneHeight = compact ? 26 : 28;
  // The grid is a 7-day timeline, so it scrolls sideways on a tablet whatever
  // happens. A lower floor just means less scrolling for denser bars.
  const gridMinWidth = tight ? 720 : compact ? 940 : 1240;

  // Drives the current-time line. One minute is plenty for a week-wide grid.
  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs()), 60000);
    return () => clearInterval(id);
  }, []);

  // Sunday-based week, matching the Area Plan Create grid.
  const weekStart = useMemo(() => selectedDate.startOf('week'), [selectedDate]);
  const weekEnd = useMemo(() => weekStart.add(TOTAL_DAYS, 'day'), [weekStart]);
  const days = useMemo(
    () => Array.from({ length: TOTAL_DAYS }, (_, i) => weekStart.add(i, 'day')),
    [weekStart]
  );

  /* ---------------------------------------------------------------- *
   *  Area rows - labelled by location, ordered by sequence
   * ---------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingAreas(true);
        const response = await fetchAreaPlannerAreaList();
        if (cancelled) return;
        const areas = (response.data || [])
          .map((area) => ({
            areaId: (area.area_id || '').trim().toUpperCase(),
            // location is the plant's own name for the room; the four rooms with
            // no machine (Sifting, Milling, Band Sealing) have none, so fall back.
            label: area.location || area.area_name || area.area_label || area.area_id,
            areaName: area.area_name || '',
            sequence:
              area.area_sequence === null || area.area_sequence === undefined
                ? Number.MAX_SAFE_INTEGER
                : Number(area.area_sequence),
          }))
          .filter((area) => area.areaId)
          .sort((a, b) => a.sequence - b.sequence || a.label.localeCompare(b.label));
        setAreaList(areas);
      } catch (err) {
        if (!cancelled) setError('Could not load the area list: ' + err.message);
      } finally {
        if (!cancelled) setLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------------------------------------------------------- *
   *  Plan and actual for the visible week
   *
   *  The endpoint buckets its response by batch match. Those buckets are
   *  flattened straight back out here: every row carrying a plan goes on the
   *  plan lane, every row carrying an actual goes on the actual lane. The
   *  union of the actual-bearing buckets is exactly the set of log book rows
   *  for that PRR code - verified against equipment_log_books directly, 0
   *  count differences across all 62 areas.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingData(true);
        setError(null);
        const response = await fetchAreaActivitiesByArea({
          start_date: weekStart.format('YYYY-MM-DD HH:mm:ss'),
          end_date: weekEnd.subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss'),
        });
        if (cancelled) return;

        // Keyed by area NAME in the response, but each entry carries area_ids -
        // re-key by code, since names are not unique and the grid keys by code.
        const byAreaId = {};
        Object.values(response.data || {}).forEach((entry) => {
          (entry.area_ids || []).forEach((code) => {
            const key = String(code || '').trim().toUpperCase();
            if (!key) return;
            if (!byAreaId[key]) byAreaId[key] = [];
            byAreaId[key].push(
              ...(entry.matched || []),
              ...(entry.not_started || []),
              ...(entry.unplanned || [])
            );
          });
        });
        setAreaData(byAreaId);
      } catch (err) {
        if (!cancelled) setError('Could not load plan vs actual: ' + err.message);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekStart, weekEnd]);

  /* ---------------------------------------------------------------- *
   *  One activity -> one positioned block (or null if it has no time
   *  on this side, or falls outside the window)
   * ---------------------------------------------------------------- */
  const makeBlock = useCallback(
    (activity, side) => {
      const isPlan = side === 'plan';
      const rawStart = isPlan ? activity.plan_start_date : activity.actual_start_time;
      const rawEnd = isPlan ? activity.plan_end_date : activity.actual_end_time;

      const start = parseApiTime(rawStart);
      if (!start) return null; // nothing on this side of the row

      // An actual with no end is still running: draw it up to now, clamped to
      // the window, rather than dropping it off the grid.
      const running = !isPlan && (rawEnd === 'running' || !rawEnd);
      let end = parseApiTime(rawEnd);
      if (!end) {
        const rightNow = dayjs();
        end = running ? (rightNow.isAfter(weekEnd) ? weekEnd : rightNow) : start.add(30, 'minute');
      }
      if (end.isBefore(start)) end = start.add(30, 'minute');

      const visibleStart = start.isBefore(weekStart) ? weekStart : start;
      const visibleEnd = end.isAfter(weekEnd) ? weekEnd : end;
      if (!visibleEnd.isAfter(visibleStart)) return null;

      const left = (visibleStart.diff(weekStart, 'minute') / TOTAL_MINUTES) * 100;
      const rawWidth = (visibleEnd.diff(visibleStart, 'minute') / TOTAL_MINUTES) * 100;
      const leftPercent = Math.max(0, Math.min(100, left));
      const widthPercent = Math.max(MIN_BLOCK_PERCENT, Math.min(100 - leftPercent, rawWidth));

      const batch =
        activity.batch_number || (isPlan ? activity.plan_product : activity.actual_product) || '';
      const activityText =
        activity.cleaning_type ||
        (isPlan ? activity.plan_activity : activity.actual_activity) ||
        '';
      const durationMins = visibleEnd.diff(visibleStart, 'minute');
      const hrs = Math.floor(durationMins / 60);
      const mins = durationMins % 60;

      return {
        key: `${side}-${activity.plan_id || 'x'}-${activity.actual_id || 'x'}-${rawStart}`,
        side,
        type: activity.activity_type || 'other',
        running,
        clippedLeft: start.isBefore(weekStart),
        clippedRight: end.isAfter(weekEnd),
        leftPercent,
        widthPercent,
        label: batch || activityText || activity.activity_type || '',
        batch,
        activityText,
        productName: activity.product_name || activity.plan_product_name || '',
        operator: isPlan ? '' : activity.start_user || '',
        duration: `${hrs}h ${String(mins).padStart(2, '0')}m`,
        timeRange:
          visibleStart.format('ddd DD MMM, HH:mm') +
          ' → ' +
          (running ? 'still running' : visibleEnd.format('HH:mm')),
      };
    },
    [weekStart, weekEnd]
  );

  const rowsByAreaId = useMemo(() => {
    const rows = {};
    Object.entries(areaData).forEach(([areaId, activities]) => {
      const plan = [];
      const actual = [];
      activities.forEach((a) => {
        const p = makeBlock(a, 'plan');
        if (p) plan.push(p);
        const c = makeBlock(a, 'actual');
        if (c) actual.push(c);
      });
      rows[areaId] = {
        plan: plan.sort((a, b) => a.leftPercent - b.leftPercent),
        actual: actual.sort((a, b) => a.leftPercent - b.leftPercent),
      };
    });
    return rows;
  }, [areaData, makeBlock]);

  /** Legend toggles drop whole activity types from both lanes. */
  const filteredRows = useMemo(() => {
    const anyMuted = Object.values(mutedTypes).some(Boolean);
    if (!anyMuted) return rowsByAreaId;
    const keep = (b) => !mutedTypes[b.type];
    const out = {};
    Object.entries(rowsByAreaId).forEach(([areaId, row]) => {
      out[areaId] = { plan: row.plan.filter(keep), actual: row.actual.filter(keep) };
    });
    return out;
  }, [rowsByAreaId, mutedTypes]);

  const emptyRow = useMemo(() => ({ plan: [], actual: [] }), []);

  /** Rooms actually drawn, after search and the activity-only filter. */
  const visibleAreas = useMemo(() => {
    const query = search.trim().toLowerCase();
    return areaList.filter((area) => {
      if (query) {
        const haystack = `${area.label} ${area.areaId} ${area.areaName}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (hideEmpty) {
        const row = filteredRows[area.areaId];
        if (!row || (row.plan.length === 0 && row.actual.length === 0)) return false;
      }
      return true;
    });
  }, [areaList, search, hideEmpty, filteredRows]);

  /** Counts reflect exactly what is on screen, so the numbers never contradict it. */
  const totals = useMemo(() => {
    let plan = 0;
    let actual = 0;
    visibleAreas.forEach((area) => {
      const row = filteredRows[area.areaId] || emptyRow;
      plan += row.plan.length;
      actual += row.actual.length;
    });
    return { plan, actual };
  }, [visibleAreas, filteredRows, emptyRow]);

  const hiddenCount = areaList.length - visibleAreas.length;
  const filtersActive =
    Boolean(search.trim()) || hiddenCount > 0 || Object.values(mutedTypes).some(Boolean);

  /** Where "now" falls across the visible week, or null if outside it. */
  const nowPercent = useMemo(() => {
    const percent = (now.diff(weekStart, 'minute') / TOTAL_MINUTES) * 100;
    return percent >= 0 && percent <= 100 ? percent : null;
  }, [now, weekStart]);

  const shiftWeek = (weeks) => setSelectedDate((d) => d.add(weeks, 'week'));

  const clearFilters = () => {
    setSearch('');
    setHideEmpty(false);
    setMutedTypes({});
  };

  const toggleType = (type) => setMutedTypes((prev) => ({ ...prev, [type]: !prev[type] }));

  // A pinned card wins over a hovered one; Escape always clears.
  const detail = pinned || hover?.block || null;
  useEffect(() => {
    if (!pinned) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPinned(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinned]);

  /** A lane of blocks. Plain function, not a component, to avoid remounts. */
  const renderLane = (blocks, side) => (
    <div
      style={{
        position: 'relative',
        height: `${laneHeight}px`,
        borderTop: side === 'actual' ? '1px dotted #d1d5db' : 'none',
        // The actual lane sits very slightly recessed, so a plan/actual pair
        // reads as one unit at a glance without adding another hard border.
        backgroundColor: side === 'actual' ? 'rgba(15,23,42,0.022)' : 'transparent',
      }}
    >
      {/* day boundaries, plus a lighter tick at midday */}
      {days.map((day, i) => (
        <React.Fragment key={i}>
          <div
            style={{
              position: 'absolute',
              left: `${(i / TOTAL_DAYS) * 100}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: '#e5e7eb',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: `${((i + 0.5) / TOTAL_DAYS) * 100}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: '#f3f4f6',
            }}
          />
          {day.isSame(now, 'day') && (
            <div
              style={{
                position: 'absolute',
                left: `${(i / TOTAL_DAYS) * 100}%`,
                width: `${(1 / TOTAL_DAYS) * 100}%`,
                top: 0,
                bottom: 0,
                backgroundColor: 'rgba(239,68,68,0.05)',
                pointerEvents: 'none',
              }}
            />
          )}
        </React.Fragment>
      ))}

      {/* current time - the reference line for reading drift */}
      {nowPercent !== null && (
        <div
          style={{
            position: 'absolute',
            left: `${nowPercent}%`,
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: '#ef4444',
            opacity: 0.75,
            pointerEvents: 'none',
            zIndex: 6,
          }}
        />
      )}

      {blocks.map((b) => {
        const isPinned = pinned && pinned.key === b.key;
        return (
          <div
            key={b.key}
            role="button"
            tabIndex={-1}
            // Hover is a mouse affordance only; on touch it either never fires
            // or fires as a phantom after the tap, which would fight the pin.
            onMouseEnter={isTouch ? undefined : (e) => setHover({ x: e.clientX, y: e.clientY, block: b })}
            onMouseMove={isTouch ? undefined : (e) => setHover({ x: e.clientX, y: e.clientY, block: b })}
            onMouseLeave={isTouch ? undefined : () => setHover(null)}
            onClick={(e) => {
              e.stopPropagation(); // the grid's own handler clears the pin
              setHover(null);
              setPinned((prev) => (prev && prev.key === b.key ? null : b));
            }}
            style={{
              position: 'absolute',
              left: `${b.leftPercent}%`,
              width: `${b.widthPercent}%`,
              top: '3px',
              height: `${laneHeight - 7}px`,
              // Colour carries the activity type and nothing else. The lane the
              // block sits in is what says plan or actual.
              backgroundColor: colorFor(b.type),
              border: isPinned ? '1.5px solid #111827' : '1px solid rgba(0,0,0,0.18)',
              // A running entry has no end yet, so it fades out rather than
              // claiming an edge it does not have.
              backgroundImage: b.running
                ? 'linear-gradient(90deg, rgba(255,255,255,0) 68%, rgba(255,255,255,0.45) 100%)'
                : 'none',
              color: '#ffffff',
              fontWeight: 600,
              borderRadius: '3px',
              borderTopRightRadius: b.clippedRight || b.running ? 0 : '3px',
              borderBottomRightRadius: b.clippedRight || b.running ? 0 : '3px',
              borderTopLeftRadius: b.clippedLeft ? 0 : '3px',
              borderBottomLeftRadius: b.clippedLeft ? 0 : '3px',
              boxShadow: isPinned
                ? '0 0 0 2px rgba(17,24,39,0.25)'
                : '0 1px 1px rgba(15,23,42,0.16)',
              fontSize: '9.5px',
              lineHeight: `${laneHeight - 11}px`,
              padding: '0 3px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              boxSizing: 'border-box',
              cursor: 'pointer',
              // Stops the browser double-tap-to-zoom delay swallowing taps.
              touchAction: 'manipulation',
              zIndex: isPinned ? 9 : 8,
            }}
          >
            {b.widthPercent > 1.1 ? b.label : ''}
          </div>
        );
      })}
    </div>
  );

  /** Legend swatch doubling as an activity-type filter. */
  const swatch = (type, text, shortText) => {
    const muted = Boolean(mutedTypes[type]);
    return (
      <button
        key={type}
        type="button"
        onClick={() => toggleType(type)}
        title={muted ? `Show ${text.toLowerCase()}` : `Hide ${text.toLowerCase()}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          marginRight: '6px',
          // Roomier hit area on touch - a 3px-tall chip is not tappable.
          padding: isTouch ? '6px 11px 6px 8px' : '3px 9px 3px 6px',
          border: `1px solid ${muted ? '#e5e7eb' : '#d1d5db'}`,
          borderRadius: '999px',
          background: muted ? '#f9fafb' : '#ffffff',
          cursor: 'pointer',
          opacity: muted ? 0.55 : 1,
          font: 'inherit',
          lineHeight: 1,
          touchAction: 'manipulation',
        }}
      >
        <span
          style={{
            width: '16px',
            height: '11px',
            borderRadius: '3px',
            display: 'inline-block',
            boxSizing: 'border-box',
            backgroundColor: muted ? 'transparent' : colorFor(type),
            border: `1px solid ${muted ? '#9ca3af' : 'rgba(0,0,0,0.18)'}`,
          }}
        />
        <span
          style={{
            fontSize: '12px',
            color: muted ? '#9ca3af' : '#374151',
            textDecoration: muted ? 'line-through' : 'none',
          }}
        >
          {tight ? shortText : text}
        </span>
      </button>
    );
  };

  /* ---------------------------------------------------------------- *
   *  Detail card - floating tooltip with a mouse, bottom sheet on a
   *  small screen where a cursor-anchored box would sit under the thumb.
   * ---------------------------------------------------------------- */
  const renderDetail = () => {
    if (!detail) return null;
    const asSheet = Boolean(pinned) && compact;

    const position = asSheet
      ? { left: '12px', right: '12px', bottom: '12px', maxWidth: 'none' }
      : {
          left: Math.min((hover?.x ?? 0) + 14, viewportWidth - 300),
          top: Math.min((hover?.y ?? 0) + 14, (typeof window === 'undefined' ? 800 : window.innerHeight) - 170),
          maxWidth: '290px',
        };

    // A pinned card is interactive (it carries a close button); a hover card
    // must never eat the pointer or it would flicker against its own bar.
    const pinnedFloating = Boolean(pinned) && !asSheet;
    const floatingPin = pinnedFloating
      ? { left: '50%', top: '16px', transform: 'translateX(-50%)', maxWidth: '320px' }
      : null;

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          ...position,
          ...(floatingPin || {}),
          zIndex: 1000,
          background: '#111827',
          color: '#f9fafb',
          padding: asSheet ? '12px 14px' : '9px 12px',
          borderRadius: '8px',
          fontSize: asSheet ? '13px' : '12px',
          lineHeight: 1.55,
          boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
          pointerEvents: pinned ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            fontWeight: 700,
            marginBottom: '5px',
            paddingBottom: '5px',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
          }}
        >
          <span
            style={{
              width: '9px',
              height: '9px',
              borderRadius: '2px',
              backgroundColor: colorFor(detail.type),
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#93c5fd' }}>{detail.side === 'plan' ? 'PLAN' : 'ACTUAL'}</span>
          <span style={{ textTransform: 'capitalize', color: '#e5e7eb', fontWeight: 600 }}>
            {detail.type}
          </span>
          {pinned && (
            <button
              type="button"
              onClick={() => setPinned(null)}
              aria-label="Close"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                fontSize: '17px',
                lineHeight: 1,
                cursor: 'pointer',
                padding: '0 2px',
                touchAction: 'manipulation',
              }}
            >
              {'×'}
            </button>
          )}
        </div>
        {detail.batch && (
          <div>
            <span style={{ color: '#9ca3af' }}>Batch: </span>
            <strong>{detail.batch}</strong>
          </div>
        )}
        {detail.activityText && (
          <div>
            <span style={{ color: '#9ca3af' }}>Activity: </span>
            <strong>{detail.activityText}</strong>
          </div>
        )}
        {detail.productName && (
          <div>
            <span style={{ color: '#9ca3af' }}>Product: </span>
            {detail.productName}
          </div>
        )}
        {detail.operator && (
          <div>
            <span style={{ color: '#9ca3af' }}>Logged by: </span>
            {detail.operator}
          </div>
        )}
        <div style={{ marginTop: '3px' }}>{detail.timeRange}</div>
        <div style={{ color: '#9ca3af' }}>
          {detail.duration}
          {detail.running ? ' · still running' : ''}
        </div>
      </div>
    );
  };

  return (
    <div
      onClick={() => setPinned(null)}
      style={{ padding: compact ? '12px' : '18px 20px', maxWidth: '100%' }}
    >
      {/* ---------------- Header ---------------- */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: compact ? '10px 12px' : '12px 18px',
          marginBottom: '12px',
        }}
      >
        {/* Row 1: title, counts, room tally. The hidden-room notice lives here
            rather than on its own line - it used to cost a whole row. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: tight ? '16px' : compact ? '18px' : '20px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {tight ? '' : '⚖️ '}Area Plan vs Actual
          </h2>
          <div
            style={{
              display: 'flex',
              gap: '7px',
              flexWrap: 'wrap',
              alignItems: 'center',
              fontSize: '12px',
            }}
          >
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {totals.plan} planned
            </Tag>
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              {totals.actual} logged
            </Tag>
            <span style={{ color: '#6b7280' }}>
              {visibleAreas.length}/{areaList.length} rooms
            </span>
            {hideEmpty && hiddenCount > 0 && !search.trim() && (
              <Button
                size="small"
                type="link"
                onClick={() => setHideEmpty(false)}
                style={{ padding: 0, fontSize: '12px', height: 'auto' }}
              >
                +{hiddenCount} empty
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: everything else. One line on a desktop; wraps by itself on a
            tablet, so no breakpoint decides the layout - only what is shown. */}
        <div
          style={{
            marginTop: '10px',
            paddingTop: '9px',
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            flexWrap: 'wrap',
            rowGap: '9px',
            columnGap: '10px',
            alignItems: 'center',
          }}
        >
          <DatePicker
            value={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            format="DD-MMM-YYYY"
            allowClear={false}
            size={compact ? 'small' : 'middle'}
            style={{ width: tight ? '135px' : 'auto' }}
          />
          <Space.Compact size={compact ? 'small' : 'middle'}>
            <Button onClick={() => shiftWeek(-1)}>{'←'}</Button>
            <Button onClick={() => setSelectedDate(dayjs())}>This Week</Button>
            <Button onClick={() => shiftWeek(1)}>{'→'}</Button>
          </Space.Compact>
          <span
            style={{
              color: '#374151',
              fontSize: '12.5px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {weekStart.format('DD MMM')}
            {'–'}
            {weekStart.add(6, 'day').format(tight ? 'DD MMM' : 'DD MMM YYYY')}
          </span>
          {loadingData && <Spin size="small" />}

          {!compact && <span style={{ width: '1px', height: '18px', backgroundColor: '#e5e7eb' }} />}

          <Input
            allowClear
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tight ? 'Search…' : 'Search room or PRR code…'}
            style={{ width: tight ? '120px' : '200px' }}
          />

          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Switch size="small" checked={hideEmpty} onChange={setHideEmpty} />
            <span style={{ fontSize: '12px', color: '#374151', whiteSpace: 'nowrap' }}>
              {tight ? 'Active only' : 'Rooms with activity only'}
            </span>
          </span>

          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {LEGEND.map(([type, text, shortText]) => swatch(type, text, shortText))}
          </span>

          {filtersActive && (
            <Button
              size="small"
              type="link"
              onClick={clearFilters}
              style={{ padding: 0, height: 'auto' }}
            >
              Clear
            </Button>
          )}

          <span
            style={{
              marginLeft: compact ? 0 : 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {!compact && (
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                {isTouch ? 'Tap a bar for details.' : 'Hover or click a bar for details.'}
              </span>
            )}
            <Button
              size="small"
              type="link"
              onClick={(e) => {
                e.stopPropagation();
                setShowHelp((v) => !v);
              }}
              style={{ padding: 0, height: 'auto' }}
            >
              {showHelp ? 'Hide help' : 'How to read this'}
            </Button>
          </span>
        </div>

        {/* The lane explanation is only worth its vertical space on demand. */}
        {showHelp && (
          <Alert
            style={{ marginTop: '10px' }}
            type="info"
            showIcon
            message="Top lane is the plan for that room. Bottom lane is every equipment log book entry recorded against that room's PRR number."
            description={`The two lanes are independent - nothing is matched between them. Compare them by eye: the gap between a top bar and the bar below it is how far the work drifted from the plan. ${
              isTouch ? 'Tap' : 'Hover or click'
            } any bar for its batch, activity and timings.`}
          />
        )}
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: '12px' }} />}

      {/* ---------------- Grid ---------------- */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {loadingAreas ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : areaList.length === 0 ? (
          <Empty description="No areas configured" style={{ padding: '40px' }} />
        ) : visibleAreas.length === 0 ? (
          <Empty
            description={
              search.trim()
                ? `No room matches "${search.trim()}"`
                : 'No room has any activity this week'
            }
            style={{ padding: '40px' }}
          >
            <Button onClick={clearFilters}>Clear filters</Button>
          </Empty>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ minWidth: `${gridMinWidth}px` }}>
              {/* Day header */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: '2px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                  position: 'sticky',
                  top: 0,
                  zIndex: 30,
                }}
              >
                <div
                  style={{
                    width: `${areaLabelWidth + laneLabelWidth}px`,
                    minWidth: `${areaLabelWidth + laneLabelWidth}px`,
                    padding: '7px 10px',
                    fontWeight: 700,
                    fontSize: '12.5px',
                    position: 'sticky',
                    left: 0,
                    zIndex: 31,
                    backgroundColor: '#f9fafb',
                    borderRight: '2px solid #d1d5db',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Area
                </div>
                <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
                  {days.map((day) => {
                    const isToday = day.isSame(now, 'day');
                    return (
                      <div
                        key={day.format('YYYY-MM-DD')}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          padding: '6px 3px',
                          fontSize: tight ? '11px' : '12px',
                          fontWeight: 600,
                          backgroundColor: isToday ? '#fef2f2' : 'transparent',
                          borderLeft: '1px solid #e5e7eb',
                          color: isToday ? '#b91c1c' : '#111827',
                        }}
                      >
                        <div>{day.format('ddd')}</div>
                        <div
                          style={{
                            fontWeight: 400,
                            fontSize: tight ? '10px' : '11.5px',
                            color: isToday ? '#dc2626' : '#6b7280',
                          }}
                        >
                          {day.format(tight ? 'DD' : 'DD MMM')}
                        </div>
                      </div>
                    );
                  })}
                  {/* "now" flag, anchored to the same line drawn down every lane */}
                  {nowPercent !== null && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${nowPercent}%`,
                        bottom: '2px',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#ef4444',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: 700,
                        letterSpacing: '.04em',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tight ? 'NOW' : `NOW ${now.format('HH:mm')}`}
                    </div>
                  )}
                </div>
              </div>

              {/* One block per area: label + two lanes */}
              {visibleAreas.map((area, index) => {
                const row = filteredRows[area.areaId] || emptyRow;
                const isHovered = hoveredArea === area.areaId;
                const zebra = index % 2 === 1;
                const bg = isHovered ? '#eff6ff' : zebra ? '#fcfcfd' : '#ffffff';
                const counts = { Plan: row.plan.length, Actual: row.actual.length };
                return (
                  <div
                    key={area.areaId}
                    onMouseEnter={isTouch ? undefined : () => setHoveredArea(area.areaId)}
                    onMouseLeave={isTouch ? undefined : () => setHoveredArea(null)}
                    style={{
                      display: 'flex',
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: bg,
                    }}
                  >
                    {/* Area label */}
                    <div
                      style={{
                        width: `${areaLabelWidth}px`,
                        minWidth: `${areaLabelWidth}px`,
                        padding: '4px 8px',
                        backgroundColor: bg,
                        position: 'sticky',
                        left: 0,
                        zIndex: 12,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                      title={
                        area.areaName
                          ? `${area.label} - ${area.areaName} (${area.areaId})`
                          : area.label
                      }
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: tight ? '11.5px' : '12.5px',
                          color: '#111827',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {area.label}
                      </div>
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>{area.areaId}</div>
                    </div>

                    {/* Plan / Actual tags, each with its own count for this room */}
                    <div
                      style={{
                        width: `${laneLabelWidth}px`,
                        minWidth: `${laneLabelWidth}px`,
                        position: 'sticky',
                        left: `${areaLabelWidth}px`,
                        zIndex: 12,
                        backgroundColor: bg,
                        borderRight: '2px solid #d1d5db',
                        flexShrink: 0,
                      }}
                    >
                      {['Plan', 'Actual'].map((tag) => (
                        <div
                          key={tag}
                          style={{
                            height: `${laneHeight}px`,
                            fontSize: '9.5px',
                            fontWeight: 700,
                            letterSpacing: '.03em',
                            color: tag === 'Plan' ? '#374151' : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: tight ? '0 5px' : '0 8px',
                            borderTop: tag === 'Actual' ? '1px dotted #d1d5db' : 'none',
                          }}
                        >
                          <span>{tight ? tag.slice(0, 3).toUpperCase() : tag.toUpperCase()}</span>
                          <span
                            style={{
                              fontWeight: 600,
                              color: counts[tag] === 0 ? '#d1d5db' : '#9ca3af',
                            }}
                          >
                            {counts[tag]}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* The two lanes */}
                    <div style={{ flex: 1, position: 'relative' }}>
                      {renderLane(row.plan, 'plan')}
                      {renderLane(row.actual, 'actual')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '9px', fontSize: '11.5px', color: '#9ca3af' }}>
        Actuals come from the equipment log book and only exist up to now, so future weeks show an
        empty actual lane.
      </div>

      {renderDetail()}
    </div>
  );
};

export default AreaPlanVsActual;
